/**
 * CORE DE EXPORTAÇÃO - Funções Utilitárias Compartilhadas
 * 
 * Este arquivo contém todas as funções e configurações centrais 
 * usadas pelos diferentes formatos de exportação (PDF, HTML, Excel, CSV).
 * 
 * FUNÇÕES PRINCIPAIS:
 * - formatAppliedFilters: Formata filtros aplicados para exibição
 * - preparePdfContent: Prepara conteúdo HTML para conversão em PDF
 * - loadImageAsDataURL: Converte imagens para base64
 * - renderFAIconToDataURL: Renderiza ícones FontAwesome como imagem
 * - getExportColumns: Obtém colunas configuradas para exportação
 * - formatDataForExport: Formata dados da grid para exportação
 * 
 * INICIALIZAÇÕES:
 * - FontAwesome: Carrega todos os ícones (fas, far, fab)
 * - html2canvas: Disponibiliza globalmente para jsPDF
 * - IMAGE_SPECS: Configurações padrão para imagens em PDFs
 * 
 * USADO POR: LayoutPdf.js, LayoutPdfHtml.js, LayoutHtml.js, LayoutExcel.js, LayoutCsv.js
 * 
 * IMPORTANTE: NÃO REMOVER - Este arquivo é essencial para todas as exportações!
 */

import { library, icon } from '@fortawesome/fontawesome-svg-core'
import * as fas from '@fortawesome/free-solid-svg-icons'
import * as far from '@fortawesome/free-regular-svg-icons'
import * as fab from '@fortawesome/free-brands-svg-icons'
import { Canvg } from 'canvg'
import html2canvas from 'html2canvas'

try {
  library.add(
    ...(Object.values(fas).filter((i) => i && i.iconName)),
    ...(Object.values(far).filter((i) => i && i.iconName)),
    ...(Object.values(fab).filter((i) => i && i.iconName))
  )
} catch (_) {}

try {
  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.html2canvas = window.html2canvas || html2canvas
  }
} catch (_) {}

export const IMAGE_SPECS = {
  IMAGE_SIZE: 11,
  IMAGE_COL_WIDTH: 14,
  IMAGE_MIN_ROW_HEIGHT: 14,
}

export const loadImageAsDataURL = (src) => {
  return new Promise((resolve) => {
    if (!src || typeof src !== 'string') return resolve(null);
    if (src.startsWith('data:image')) return resolve(src);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = function () { resolve(null); };
    img.src = src;
  });
};

export const resolveColorFromClass = (cls) => {
  const s = (cls || '').toLowerCase();
  const rgb = s.includes('vermelho') || s.includes('danger') || s.includes('times') || s.includes('ban')
    ? [220, 53, 69]
    : s.includes('verde') || s.includes('success') || s.includes('check')
    ? [40, 167, 69]
    : s.includes('amarelo') || s.includes('warning') || s.includes('exclamation')
    ? [255, 193, 7]
    : s.includes('azul') || s.includes('primary') || s.includes('info')
    ? [0, 123, 255]
    : s.includes('cinza') || s.includes('secondary') || s.includes('gray')
    ? [108, 117, 125]
    : s.includes('preto') || s.includes('dark')
    ? [33, 37, 41]
    : s.includes('laranja')
    ? [253, 126, 20]
    : [108, 117, 125];
  return { rgb, css: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` };
};

export const renderFAIconToDataURL = async (faClass, sizePx = 64, color = '#6c757d') => {
  try {
    if (!faClass) return null;
    const parts = faClass.split(/\s+/).filter(Boolean);
    const style = parts.find((p) => p === 'fas' || p === 'far' || p === 'fab') || 'fas';
    const nameClass = parts.find((p) => p.startsWith('fa-')) || '';
    if (!nameClass) return null;
    const name = nameClass.replace('fa-', '');
    const faIcon = icon({ prefix: style, iconName: name }, { classes: [], styles: { color } });
    if (!faIcon || !faIcon.html || !faIcon.html[0]) return null;
    const svg = faIcon.html[0];
    const canvas = document.createElement('canvas');
    canvas.width = sizePx;
    canvas.height = sizePx;
    const ctx = canvas.getContext('2d');
    const v = await Canvg.from(ctx, svg);
    await v.render();
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};

export const preparePdfContent = async (dataResult, exportColumns) => {
  const imageMap = new Map();
  const imageColumnIndexes = new Set();
  const tableData = await Promise.all(
    dataResult.map(async (row, rowIdx) => {
      return Promise.all(
        exportColumns.map(async (col, colIdx) => {
          let value = row[col.field] || '';

          const hasImageFlag = (col.hasImage === 'True' || col.hasImage === true || col.HasImage === true);
          if (hasImageFlag) {
            const imageSrcCandidates = [];
            const iconClassCandidates = [];
            const textCandidates = [];
            if (typeof value === 'string') {
              const trimmed = value.trim();
              if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  if (Array.isArray(parsed)) {
                    parsed.forEach((it) => {
                      if (it?.tipo === 'image' && it?.valor) imageSrcCandidates.push(it.valor);
                      if (it?.tipo === 'icone' && it?.valor) iconClassCandidates.push(it.valor);
                      if (it?.tipo === 'texto' && it?.valor != null && it.valor !== '') textCandidates.push(String(it.valor));
                    });
                  } else {
                    if (parsed?.tipo === 'image' && parsed?.valor) imageSrcCandidates.push(parsed.valor);
                    if (parsed?.tipo === 'icone' && parsed?.valor) iconClassCandidates.push(parsed.valor);
                    if (parsed?.tipo === 'texto' && parsed?.valor != null && parsed.valor !== '') textCandidates.push(String(parsed.valor));
                    if (!parsed?.tipo && parsed?.valor) imageSrcCandidates.push(parsed.valor);
                  }
                } catch (_) {
                  imageSrcCandidates.push(trimmed);
                }
              } else {
                imageSrcCandidates.push(trimmed);
              }
            }

            const cellKey = `${rowIdx}-${colIdx}`;
            const dataUrls = [];
            for (const src of imageSrcCandidates) {
              const du = await loadImageAsDataURL(src);
              if (du) dataUrls.push(du);
            }
            for (const icn of iconClassCandidates) {
              const { css } = resolveColorFromClass(icn);
              const du = await renderFAIconToDataURL(icn, 64, css);
              if (du) dataUrls.push(du);
            }
            if (dataUrls.length) {
              imageMap.set(cellKey, dataUrls);
              imageColumnIndexes.add(colIdx);
            }
            return textCandidates.join(' ');
          } else {
            if (typeof value === 'string') {
              const cleaned = value.replace(/[^\x20-\x7E\n\r\t]/g, '');
              const trimmed = cleaned.trim();
              if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  const iconClasses = [];
                  if (Array.isArray(parsed)) {
                    parsed.forEach((it) => { if (it?.tipo === 'icone' && it?.valor) iconClasses.push(it.valor); });
                  } else if (parsed?.tipo === 'icone' && parsed?.valor) {
                    iconClasses.push(parsed.valor);
                  }
                  if (iconClasses.length) {
                    const urls = [];
                    for (const icn of iconClasses) {
                      const { css } = resolveColorFromClass(icn);
                      const du = await renderFAIconToDataURL(icn, 64, css);
                      if (du) urls.push(du);
                    }
                    if (urls.length) {
                      imageMap.set(`${rowIdx}-${colIdx}`, urls);
                      imageColumnIndexes.add(colIdx);
                      return '';
                    }
                  }
                } catch (_) {}
              }
              return cleaned.substring(0, 50);
            }
            return String(value).substring(0, 50);
          }
        })
      );
    })
  );

  return { tableData, imageMap, imageColumnIndexes };
}

export const getPdfColumnStyles = (imageColumnIndexes, imageColWidth, exportColumns = []) => {
  const columnStyles = {};
  
  // Aplicar larguras das colunas de imagem
  imageColumnIndexes.forEach((idx) => {
    columnStyles[idx] = { cellWidth: imageColWidth, halign: 'center', valign: 'middle' };
  });
  
  // Aplicar larguras customizadas das colunas se definidas
  exportColumns.forEach((col, idx) => {
    if (col.width && !columnStyles[idx]) {
      let width = col.width;
      if (typeof width === 'string') {
        if (width.includes('px')) {
          // Converter pixels para mm (aproximadamente)
          const pxValue = parseInt(width.replace('px', ''));
          width = Math.max(10, Math.round(pxValue * 0.264583)); // 1px ≈ 0.264583mm
        } else if (width.includes('%')) {
          // Porcentagem: usar largura proporcional da página
          const percentValue = parseInt(width.replace('%', ''));
          width = Math.max(10, Math.round((percentValue / 100) * 200)); // Base de 200mm
        } else if (width.includes('mm')) {
          width = Math.max(10, parseInt(width.replace('mm', '')));
        } else {
          // Se for só número, assumir que já está em mm
          width = Math.max(10, parseInt(width) || 30);
        }
      } else if (typeof width === 'number') {
        width = Math.max(10, width);
      } else {
        width = 30; // valor padrão em mm
      }
      
      columnStyles[idx] = { cellWidth: width, halign: 'left', valign: 'middle' };
    }
  });
  
  return columnStyles;
}

export const getExportColumns = (gridComponents) => {
  try {
    const tableComponent = gridComponents?.BODY[0]?.Childrens?.find(
      (c) => c.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_001"
    );
    if (tableComponent && tableComponent.Childrens) {
      const columnsConfigChild = tableComponent.Childrens.find(
        child => child.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_LISTA_COLUNAS"
      );
      if (columnsConfigChild && columnsConfigChild.Valor_Componente) {
        const columnsConfig = JSON.parse(columnsConfigChild.Valor_Componente);
        const exportColumns = columnsConfig.filter(column => 
          (column.Export === "True" || column.Export === true) && 
          column.hasImage !== "True" && 
          column.hasImage !== true
        ).map(column => ({
          field: column.field,
          header: column.header
        }));
        return exportColumns;
      }
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const formatDataForExport = (data, columns) => {
  if (!data || !columns) return [];
  return data.map(item => {
    const formattedItem = {};
    columns.forEach(column => {
      formattedItem[column.header] = item[column.field] || '';
    });
    return formattedItem;
  });
};

export const filterDataForExport = (data, columns) => {
  if (!data || !columns) return [];
  return data.map(item => {
    const filteredItem = {};
    columns.forEach(column => {
      let value = item[column.field] || '';
      if (typeof value === 'string' && (
        value.includes('"tipo": "icone"') || 
        value.includes('"tipo": "image"') ||
        column.field.toLowerCase().includes('foto') ||
        column.field.toLowerCase().includes('photo') ||
        column.field.toLowerCase().includes('imagem') ||
        column.field.toLowerCase().includes('image') ||
        value.length > 10000
      )) {
        try {
          JSON.parse(value);
          filteredItem[column.field] = value;
        } catch (e) {
          if (value.length > 1000 || value.startsWith('data:image')) {
            filteredItem[column.field] = '📷 Imagem';
          } else if (column.field.toLowerCase().includes('foto') || column.field.toLowerCase().includes('photo')) {
            filteredItem[column.field] = '📷 Foto';
          } else {
            filteredItem[column.field] = value;
          }
        }
      } else {
        if (typeof value === 'string') {
          value = value
            .replace(/[^\x20-\x7F]/g, '')
            .replace(/[Ø=Ü÷]/g, '');
          if (value.length > 32000) {
            value = value.substring(0, 32000) + '... [TRUNCADO]';
          }
        }
        filteredItem[column.field] = value;
      }
    });
    return filteredItem;
  });
};

export const formatAppliedFilters = (appliedFilters) => {
  if (!appliedFilters || !appliedFilters[0] || Object.keys(appliedFilters[0]).length === 0) {
    return null;
  }
  
  const filters = [];
  const filterObj = appliedFilters[0];
  
  Object.keys(filterObj).forEach(key => {
    const filter = filterObj[key];
    
    if (Array.isArray(filter)) {
      // Para filtros de múltipla seleção
      if (filter[0]?.values && filter[0]?.field) {
        const values = filter[0].values.map(v => v.label || v.value).join(', ');
        filters.push(`${filter[0].field}: ${values}`);
      }
    } else if (filter && typeof filter === 'object') {
      // Para filtros simples
      const value = filter.label || filter.value;
      const field = filter.field || key;
      if (value) {
        filters.push(`${field}: ${value}`);
      }
    }
  });
  
  return filters.length > 0 ? filters.join(' | ') : null;
};

export const getPDFExportColumns = (gridComponents) => {
  try {
    const tableComponent = gridComponents?.BODY[0]?.Childrens?.find(
      (c) => c.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_001"
    );
    if (tableComponent && tableComponent.Childrens) {
      const columnsConfigChild = tableComponent.Childrens.find(
        child => child.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_LISTA_COLUNAS"
      );
      if (columnsConfigChild && columnsConfigChild.Valor_Componente) {
        const columnsConfig = JSON.parse(columnsConfigChild.Valor_Componente);
        const pdfColumns = columnsConfig.filter(column => 
          (column.Export === "True" || column.Export === true)
        ).map(column => ({
          field: column.field,
          header: column.header,
          HasImage: column.hasImage === "True" || column.hasImage === true,
          Export: column.Export
        }));
        return pdfColumns;
      }
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const getAllColumns = (gridComponents) => {
  try {
    const tableComponent = gridComponents?.BODY[0]?.Childrens?.find(
      (c) => c.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_001"
    );
    if (tableComponent && tableComponent.Childrens) {
      const columnsConfigChild = tableComponent.Childrens.find(
        child => child.Chave_Transacao_Componente === "TABELA_RESULTADOS_GRID_LISTA_COLUNAS"
      );
      if (columnsConfigChild && columnsConfigChild.Valor_Componente) {
        const columnsConfig = JSON.parse(columnsConfigChild.Valor_Componente);
        const allColumns = columnsConfig.map(column => ({
          field: column.field,
          header: column.header
        }));
        return allColumns;
      }
    }
    return [];
  } catch (error) {
    return [];
  }
};


